/* ============================================================
   HEMP HOUNDS — UGC · SHOPPABLE VIDEO REEL
   Self-contained, re-init-safe, NO library dependency. The reel is a
   genuinely seamless infinite marquee: measured DOM clones + a single
   rAF loop translating the track (drift + drag + momentum + arrows).
   Handles skeleton loading, hover/in-view autoplay, per-card mute, and
   the lightbox player. Add-to-cart is left to the theme's global
   delegation (hh-common.js → .prod-add-form).
   ============================================================ */
(function () {
  'use strict';

  /* STAR FILL — the JS twin of snippets/hh-stars.liquid. The star glyph's ink spans only x=2.6..21.5
     of its 0..24 tile, so the naive --v/5 boundary overshoots and paints 4.8-5.0 as a solid five
     stars. Land it inside the ink instead: floor(v) whole stars, then frac across the ink width.
     Keep these three constants in step with the snippet if the star path ever changes. */
  function starFill(v) {
    v = parseFloat(v) || 0;
    if (v >= 5) return '100%';
    if (v <= 0) v = 0;
    var full = Math.floor(v);
    return ((full + 0.1083333 + (v - full) * 0.7875) / 5 * 100).toFixed(3) + '%';
  }

  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover = window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches;

  function init(root) {
    if (!root || root.__hhUgcReady) return;
    root.__hhUgcReady = true;

    var reel = root.querySelector('.hh-ugc-reel');
    var cards = [].slice.call(root.querySelectorAll('.hh-ugc-card'));
    if (!reel || !cards.length) return;

    var lb = root.querySelector('.hh-ugc-lb');
    var lightboxOn = root.getAttribute('data-lightbox') !== 'false';
    var soundOn = root.getAttribute('data-lb-sound') !== 'false';
    var autoScroll = root.getAttribute('data-auto-scroll') !== 'false';      // reel drift — default ON
    var videoAutoplay = root.getAttribute('data-video-autoplay') === 'true';  // touch in-view preview — default OFF
    var vidOf = function (c) { return c.querySelector('.hh-ugc-vid'); };

    /* Tag each card with its ORIGINAL index so a click on any card — original OR a marquee
       clone — opens the right lightbox item. `cards` stays the ORIGINALS snapshot (captured
       above) for the lightbox's own prev/next cycling. Card interactions are DELEGATED on the
       track, so they automatically cover the clones the marquee appends (clones inherit data-ugc-i). */
    cards.forEach(function (c, i) { c.setAttribute('data-ugc-i', i); });
    var track = root.querySelector('.hh-ugc-track') || reel;
    function slidesAll() { return [].slice.call(reel.querySelectorAll('.hh-ugc-card')); }

    /* ---------- inline playback ---------- */
    function playInline(c) {
      var v = vidOf(c);
      if (!v || !v.getAttribute('src')) return;
      v.muted = !c.classList.contains('is-unmuted');
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
      c.classList.add('is-playing');
    }
    function pauseInline(c, reset) {
      var v = vidOf(c);
      if (!v) return;
      v.pause();
      c.classList.remove('is-playing');
      if (reset) { try { v.currentTime = 0; } catch (e) {} }
    }
    function muteOthers(except) {
      slidesAll().forEach(function (c) {
        if (c === except) return;
        c.classList.remove('is-unmuted');
        var v = vidOf(c); if (v) v.muted = true;
      });
    }
    function toggleMute(c) {
      var v = vidOf(c); if (!v) return;
      if (c.classList.contains('is-unmuted')) { c.classList.remove('is-unmuted'); v.muted = true; }
      else { muteOthers(c); c.classList.add('is-unmuted'); v.muted = false; playInline(c); }
    }

    /* ---------- delegated card interactions (cover the marquee's clones too) ---------- */
    track.addEventListener('click', function (e) {
      var c = e.target.closest('.hh-ugc-card'); if (!c || !track.contains(c)) return;
      if (e.target.closest('.hh-ugc-shop')) return;                    // shop bar keeps its own link/add
      if (e.target.closest('.hh-ugc-mute')) { e.stopPropagation(); toggleMute(c); return; }
      if (lightboxOn && lb) openLb(+c.getAttribute('data-ugc-i') || 0);
      else if (c.classList.contains('is-playing')) pauseInline(c, false);
      else playInline(c);
    });
    if (canHover) {
      track.addEventListener('pointerover', function (e) { var c = e.target.closest('.hh-ugc-card'); if (c && !c.__hv) { c.__hv = 1; playInline(c); } });
      track.addEventListener('pointerout', function (e) { var c = e.target.closest('.hh-ugc-card'); if (c && !c.contains(e.relatedTarget)) { c.__hv = 0; pauseInline(c, false); } });
    }

    /* ---------- skeleton: reveal each card when its poster loads (originals + clones) ---------- */
    function skeleton() {
      slidesAll().forEach(function (c) {
        if (c.__hhSkel) return; c.__hhSkel = 1;
        var poster = c.querySelector('.hh-ugc-poster');
        var done = function () { c.classList.add('is-loaded'); };
        if (!poster) { done(); return; }
        if (poster.complete && poster.naturalWidth) done();
        else { poster.addEventListener('load', done); poster.addEventListener('error', done); }
      });
    }

    /* ============================================================
       INFINITE REEL — custom rAF marquee (no library dependency).
       Measured clones give a seamless modulo-wrap; drift + drag + momentum
       + arrows all mutate ONE normalized accumulator `x`. Card widths are
       CSS-fixed so a single measure (+ resize rebuild) is exact — it can
       NEVER lock or show an empty edge. Reduced-motion / < 3 cards fall
       back to a native scroll-snap rail whose arrows use scrollBy.
       ============================================================ */
    var prevA = root.querySelector('[data-reel-prev]');
    var nextA = root.querySelector('[data-reel-next]');
    var activeCard = null;
    var ORIG = cards.length;
    var native = reduce || ORIG < 3;

    /* teardown registry — theme-editor re-inits / section removal must not leak */
    var cleanups = [];
    function onWin(type, fn, opts) { window.addEventListener(type, fn, opts); cleanups.push(function () { window.removeEventListener(type, fn, opts); }); }
    function onDoc(type, fn, opts) { document.addEventListener(type, fn, opts); cleanups.push(function () { document.removeEventListener(type, fn, opts); }); }
    root.__hhUgcCleanup = function () { cleanups.forEach(function (fn) { try { fn(); } catch (e) {} }); cleanups.length = 0; };

    /* ---- opt-in touch in-view video preview (default OFF) — shared by BOTH modes ---- */
    var settleT = null;
    function playCentered() {
      if (!videoAutoplay || canHover || reduce) return;
      var rr = reel.getBoundingClientRect(), mid = rr.left + rr.width / 2, best = null, bestD = 1e9;
      slidesAll().forEach(function (s) { var b = s.getBoundingClientRect(); var d = Math.abs(b.left + b.width / 2 - mid); if (d < bestD) { bestD = d; best = s; } });
      if (!best || best === activeCard) return;
      activeCard = best;
      slidesAll().forEach(function (s) { if (s !== best) pauseInline(s, false); });
      if (lb && lb.getAttribute('data-open') === 'true') return;
      playInline(best);
    }
    function scheduleSettle() { if (settleT) clearTimeout(settleT); settleT = setTimeout(playCentered, 500); }
    cleanups.push(function () { if (settleT) clearTimeout(settleT); });

    if (native) {
      /* ---- native scroll-snap rail (accessible; arrows via scrollBy) ---- */
      reel.classList.add('is-native');
      var nativeStep = function () { var c = reel.querySelector('.hh-ugc-card'); return c ? c.offsetWidth + 24 : 300; };
      if (prevA) prevA.addEventListener('click', function () { reel.scrollBy({ left: -nativeStep(), behavior: reduce ? 'auto' : 'smooth' }); });
      if (nextA) nextA.addEventListener('click', function () { reel.scrollBy({ left: nativeStep(), behavior: reduce ? 'auto' : 'smooth' }); });
      skeleton();
      /* honour video_autoplay in native mode too (no-op unless the setting is on AND touch device) */
      if (videoAutoplay && !reduce && !canHover) {
        var nio = new IntersectionObserver(function (ents) { if (ents[0].isIntersecting) scheduleSettle(); }, { threshold: 0 });
        nio.observe(reel);
        cleanups.push(function () { nio.disconnect(); });
      }
    } else {
      /* ---- marquee engine ---- */
      var S = 0, stepW = 0, x = 0, prevXApplied = -1;
      var DRIFT = 0.045;                       // px per ms (~45px/s) — gentle, premium
      var DRAG_THRESH = 6;                      // px before a press becomes a drag (below = tap)
      var raf = null, rafLast = 0, inView = true;
      var dragging = false, captured = false, hoverPaused = false, dragMoved = 0;
      var easing = false, easeFrom = 0, easeTo = 0, easePrev = 0, easeT0 = 0, easeDur = 460;
      var momenting = false, momVel = 0;
      var startClientX = 0, startX = 0, downClientX = 0, lastPX = 0, lastPT = 0, lastVel = 0;

      function easeOutCubic(p) { return 1 - Math.pow(1 - p, 3); }
      function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

      function buildTrack() {
        // strip any prior clones — keep only the ORIG originals
        var kids = [].slice.call(track.children);
        for (var i = kids.length - 1; i >= ORIG; i--) track.removeChild(kids[i]);
        if (track.children.length < ORIG) return;   // markup not ready yet
        var first = track.children[0], last = track.children[ORIG - 1];
        var cs = getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
        var contentW = (last.offsetLeft + last.offsetWidth) - first.offsetLeft;
        if (contentW <= 0) return;               // not laid out yet
        var provS = contentW + gap;
        var vw = reel.clientWidth || window.innerWidth || provS;
        // a flex row of N cards has only N-1 gaps (no trailing gap), so the rendered track is
        // copies*provS - gap. Seamless wrap needs trackWidth >= vw + S; the extra `+ gap` here
        // guarantees that at every viewport width (incl. exact multiples of the set pitch).
        var copies = Math.max(2, Math.ceil((vw + provS + gap) / provS));
        var frag = document.createDocumentFragment();
        for (var c = 1; c < copies; c++) {
          for (var j = 0; j < ORIG; j++) {
            var cl = track.children[j].cloneNode(true);
            cl.removeAttribute('id');
            cl.setAttribute('data-clone', '1');
            cl.setAttribute('aria-hidden', 'true');            // visual duplicate — hide from AT
            [].slice.call(cl.querySelectorAll('[id]')).forEach(function (n) { n.removeAttribute('id'); });
            // keep the originals as the only tab stops; clones stay mouse/touch-clickable via delegation
            [].slice.call(cl.querySelectorAll('a,button,input,video,[tabindex]')).forEach(function (n) { n.setAttribute('tabindex', '-1'); });
            frag.appendChild(cl);
          }
        }
        track.appendChild(frag);
        // TRUE seamless period = distance from original[0] to the first clone (never a computed width)
        S = track.children[ORIG].offsetLeft - track.children[0].offsetLeft;
        if (!S || S < 1) S = provS;
        stepW = S / ORIG;
        x = ((x % S) + S) % S;
        prevXApplied = -1;
        skeleton();   // wire the freshly-minted clone posters so they reveal (idempotent for surviving originals)
      }

      function driftOn() { return autoScroll && !hoverPaused && !dragging && !easing && !momenting && inView && !document.hidden; }

      function frame(ts) {
        if (!reel.isConnected) { stopLoop(); return; }
        if (!rafLast) rafLast = ts;
        var dt = Math.min(ts - rafLast, 50); rafLast = ts;
        if (dragging) { /* x is set directly in pointermove */ }
        else if (easing) {
          var p = clamp01((ts - easeT0) / easeDur);
          var pos = easeFrom + (easeTo - easeFrom) * easeOutCubic(p);
          x += (pos - easePrev); easePrev = pos;
          if (p >= 1) easing = false;
        } else if (momenting) {
          x += momVel * dt; momVel *= Math.pow(0.995, dt);
          if (Math.abs(momVel) < 0.003) momenting = false;
        } else if (driftOn()) {
          x += DRIFT * dt;
        }
        if (S > 0) x = ((x % S) + S) % S;
        if (x !== prevXApplied) { track.style.transform = 'translate3d(' + (-x) + 'px,0,0)'; prevXApplied = x; }
        raf = requestAnimationFrame(frame);
      }
      function startLoop() { if (raf == null) { rafLast = 0; raf = requestAnimationFrame(frame); } }
      function stopLoop() { if (raf != null) { cancelAnimationFrame(raf); raf = null; } }

      /* ---- arrows: nudge one card with an eased delta (never disabled — infinite) ---- */
      function startEase(d) { easing = true; momenting = false; easeFrom = 0; easeTo = d; easePrev = 0; easeT0 = performance.now(); startLoop(); }
      if (prevA) prevA.addEventListener('click', function () { startEase(-stepW || -300); });
      if (nextA) nextA.addEventListener('click', function () { startEase(stepW || 300); });

      /* ---- pointer drag (mouse + touch). Capture only AFTER the press becomes a drag, so
             taps on cards / shop links / buttons still fire their own click. ---- */
      track.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        dragging = true; captured = false; dragMoved = 0;
        downClientX = e.clientX; startClientX = e.clientX; startX = x;
        lastPX = e.clientX; lastPT = performance.now(); lastVel = 0;
        momenting = false; easing = false;
        startLoop();
      });
      track.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        dragMoved = Math.max(dragMoved, Math.abs(e.clientX - downClientX));
        if (!captured) {
          if (dragMoved <= DRAG_THRESH) return;
          captured = true; startClientX = e.clientX; startX = x;   // re-anchor so there's no jump
          try { track.setPointerCapture(e.pointerId); } catch (err) {}
        }
        x = startX - (e.clientX - startClientX);
        var now = performance.now(), dtm = now - lastPT;
        if (dtm > 0) { lastVel = -(e.clientX - lastPX) / dtm; lastPX = e.clientX; lastPT = now; }
      });
      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        if (captured) { try { track.releasePointerCapture(e.pointerId); } catch (err) {} }
        if (captured && Math.abs(lastVel) > 0.02) { momenting = true; momVel = Math.max(-3, Math.min(3, lastVel)); }
        // clear the drag flag so a stale value can't later swallow a keyboard/programmatic click.
        // On a real drag the mouse's trailing click fires almost immediately, so keep the guard a beat.
        if (captured) setTimeout(function () { dragMoved = 0; }, 350);
        else dragMoved = 0;
        startLoop();
      }
      /* release listeners live on WINDOW, not the track: a sub-threshold press that lifts off the
         track would otherwise never clear `dragging` and would FREEZE the drift. endDrag no-ops when idle. */
      onWin('pointerup', endDrag);
      onWin('pointercancel', endDrag);
      /* Suppress the click / <a> navigation that fires right after a real drag (capture phase,
         before the delegated card handler and before any link) */
      reel.addEventListener('click', function (e) { if (dragMoved > DRAG_THRESH) { e.preventDefault(); e.stopPropagation(); dragMoved = 0; } }, true);

      /* ---- hover pauses the drift (desktop only) ---- */
      if (canHover) {
        reel.addEventListener('pointerenter', function () { hoverPaused = true; });
        reel.addEventListener('pointerleave', function () { hoverPaused = false; startLoop(); });
      }

      /* ---- observers ---- */
      var io = new IntersectionObserver(function (ents) {
        inView = ents[0].isIntersecting;
        if (inView) { startLoop(); scheduleSettle(); } else stopLoop();
      }, { threshold: 0 });
      io.observe(reel);
      cleanups.push(function () { io.disconnect(); });

      var rzT = null, lastVW = 0;
      var ro = new ResizeObserver(function () {
        if (rzT) clearTimeout(rzT);
        rzT = setTimeout(function () {
          var vw = reel.clientWidth;
          if (vw === lastVW) return;             // observe VIEWPORT (not track) → no self-trigger loop
          lastVW = vw;
          stopLoop(); buildTrack(); startLoop();
        }, 150);
      });
      ro.observe(reel);
      cleanups.push(function () { ro.disconnect(); if (rzT) clearTimeout(rzT); });
      cleanups.push(stopLoop);

      /* ---- boot: defer a frame so CSS/layout is settled; re-measure once on load ---- */
      requestAnimationFrame(function () { lastVW = reel.clientWidth; buildTrack(); skeleton(); startLoop(); });
      onWin('load', function () { if (reel.isConnected) { stopLoop(); buildTrack(); startLoop(); } });
    }

    /* ---------- lightbox ---------- */
    if (!lightboxOn || !lb) return;
    var lbVid = lb.querySelector('.hh-ugc-lb-vid');
    var lbIndex = 0, lbLastFocus = null;
    var $ = function (s) { return lb.querySelector(s); };

    function setToggle() { lb.classList.toggle('is-paused', lbVid.paused); }

    function fillLb(c) {
      var d = c.dataset;
      // media
      lbVid.poster = d.poster || '';
      if (d.video) { lbVid.src = d.video; } else { lbVid.removeAttribute('src'); lbVid.load(); }
      lb.querySelector('.hh-ugc-lb-toggle').style.display = d.video ? '' : 'none';
      lb.querySelector('.hh-ugc-lb-mute').style.display = d.video ? '' : 'none';
      // handle / verified
      $('.hh-ugc-lb-cred .hh-ugc-handle').textContent = d.handle || '';
      var ver = $('.hh-ugc-lb-cred .hh-ugc-verified');
      if (ver) ver.style.display = (d.verified === 'true') ? '' : 'none';
      var lbCred = lb.querySelector('.hh-ugc-lb-cred');
      lbCred.style.display = d.handle ? '' : 'none';
      // optional profile picture — show the round avatar + frosted pill only when provided
      var lbAv = lbCred.querySelector('.hh-ugc-avatar');
      var lbAvImg = lbAv ? lbAv.querySelector('img') : null;
      if (lbAv && lbAvImg) {
        if (d.avatar) { lbAvImg.src = d.avatar; lbAvImg.alt = d.handle || ''; lbAv.style.display = ''; lbCred.classList.add('has-avatar'); }
        else { lbAvImg.removeAttribute('src'); lbAv.style.display = 'none'; lbCred.classList.remove('has-avatar'); }
      }
      // product panel
      var hasProduct = d.shop === 'product' || d.shop === 'manual';
      lb.setAttribute('data-has-product', hasProduct ? 'true' : 'false');
      if (hasProduct) {
        var img = $('.hh-ugc-lb-thumb img');
        if (d.pimg) { img.src = d.pimg; img.alt = d.pname || ''; img.style.display = ''; }
        else { img.removeAttribute('src'); img.style.display = 'none'; }
        $('.hh-ugc-lb-name').textContent = d.pname || '';
        $('.hh-ugc-lb-price').textContent = d.pprice || '';
        $('.hh-ugc-lb-price').style.display = d.pprice ? '' : 'none';
        var rateWrap = $('.hh-ugc-lb-rate');
        if (d.prating) { rateWrap.style.display = ''; var lbStars = $('.hh-ugc-lb-rate .rating');
          lbStars.style.setProperty('--v', d.prating);
          lbStars.style.setProperty('--hh-fill', starFill(d.prating)); $('.hh-ugc-lb-rate small').textContent = d.prating; }
        else { rateWrap.style.display = 'none'; }
        $('.hh-ugc-lb-caption').textContent = d.caption || '';
        $('.hh-ugc-lb-caption').style.display = d.caption ? '' : 'none';
        // benefits
        var bl = $('.hh-ugc-lb-benefits');
        bl.innerHTML = '';
        (d.benefits || '').split('|').forEach(function (t) {
          t = t.replace(/\s+$/, '').replace(/^\s+/, '');
          if (!t) return;
          var li = document.createElement('li');
          var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          s.innerHTML = '<use href="#s-check-circle"></use>';
          var span = document.createElement('span');
          span.textContent = t;
          li.appendChild(s); li.appendChild(span); bl.appendChild(li);
        });
        // view link
        var view = $('.hh-ugc-lb-view');
        if (d.purl) { view.href = d.purl; view.style.display = ''; } else { view.style.display = 'none'; }
        // add-to-cart form
        var form = $('.hh-ugc-lb-form');
        var idInput = $('.hh-ugc-lb-form input[name="id"]');
        var addBtn = $('.hh-ugc-lb-add');
        if (d.shop === 'product' && d.variant) {
          form.style.display = '';
          idInput.value = d.variant;
          addBtn.innerHTML = (d.addText || 'add to cart') + '<svg><use href="#s-plus"></use></svg>';
          addBtn.disabled = false;
        } else {
          form.style.display = 'none';
        }
      }
    }

    function openLb(i) {
      lbIndex = (i + cards.length) % cards.length;
      slidesAll().forEach(function (c) { pauseInline(c, false); });   // pause originals AND clones (no audio bleed behind the player)
      fillLb(cards[lbIndex]);
      lbLastFocus = document.activeElement;
      lb.removeAttribute('hidden');
      lb.setAttribute('data-open', 'true');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function () { lb.classList.add('is-open'); });
      if (lbVid.getAttribute('src')) {
        lbVid.muted = !soundOn;
        lb.classList.toggle('is-unmuted', soundOn);
        var p = lbVid.play();
        if (p && p.catch) p.catch(function () { lbVid.muted = true; lb.classList.remove('is-unmuted'); lbVid.play().catch(function () {}); });
      }
      setTimeout(setToggle, 60);
      var close = lb.querySelector('.hh-ugc-lb-close');
      if (close) setTimeout(function () { close.focus(); }, 160);
    }
    function closeLb() {
      lb.classList.remove('is-open');
      lb.setAttribute('data-open', 'false');
      try { lbVid.pause(); } catch (e) {}
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      setTimeout(function () { lb.setAttribute('hidden', ''); lbVid.removeAttribute('src'); try { lbVid.load(); } catch (e) {} }, 320);
      if (lbLastFocus && lbLastFocus.focus) lbLastFocus.focus();
      if (activeCard && !canHover && !reduce && videoAutoplay) playInline(activeCard);
    }
    function stepLb(dir) { openLb(lbIndex + dir); }

    [].slice.call(lb.querySelectorAll('[data-lb-close]')).forEach(function (el) { el.addEventListener('click', closeLb); });
    var lp = lb.querySelector('[data-lb-prev]'); if (lp) lp.addEventListener('click', function () { stepLb(-1); });
    var ln = lb.querySelector('[data-lb-next]'); if (ln) ln.addEventListener('click', function () { stepLb(1); });
    var lm = lb.querySelector('.hh-ugc-lb-mute'); if (lm) lm.addEventListener('click', function () { lbVid.muted = !lbVid.muted; lb.classList.toggle('is-unmuted', !lbVid.muted); });
    var lt = lb.querySelector('.hh-ugc-lb-toggle'); if (lt) lt.addEventListener('click', function () { if (lbVid.paused) { lbVid.play().catch(function () {}); } else { lbVid.pause(); } setToggle(); });
    lbVid.addEventListener('play', setToggle);
    lbVid.addEventListener('pause', setToggle);

    lb.__hhKeydown = function (e) {
      if (lb.getAttribute('data-open') !== 'true') return;
      if (e.key === 'Escape') closeLb();
      else if (e.key === 'ArrowLeft') stepLb(-1);
      else if (e.key === 'ArrowRight') stepLb(1);
    };
    onDoc('keydown', lb.__hhKeydown);

    /* close the lightbox once an add succeeds (cart drawer takes over) */
    lb.__hhCartClose = function () { if (lb.getAttribute('data-open') === 'true') closeLb(); };
    onDoc('cart:refresh', lb.__hhCartClose);

    /* if the section is torn down (theme editor) WHILE the lightbox is open, un-lock the page
       scroll synchronously — closeLb()'s 320ms timer would dangle on a detached node. */
    cleanups.push(function () {
      if (lb.getAttribute('data-open') === 'true') {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        lb.setAttribute('data-open', 'false');
        try { lbVid.pause(); lbVid.removeAttribute('src'); lbVid.load(); } catch (e) {}
      }
    });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('.hh-ugc');
    [].slice.call(roots).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  /* The section's <script defer> re-executes when Shopify swaps section markup in the theme
     editor — bind these document-level listeners only ONCE so they don't stack up. */
  function sectionRoot(e) { return e.target && e.target.querySelector ? (e.target.querySelector('.hh-ugc') || (e.target.classList && e.target.classList.contains('hh-ugc') ? e.target : null)) : null; }
  if (!window.__hhUgcGlobalBound) {
    window.__hhUgcGlobalBound = true;
    /* theme editor: tear down the old instance, then (re)init the fresh markup */
    document.addEventListener('shopify:section:load', function (e) {
      var el = sectionRoot(e);
      if (el) { if (el.__hhUgcCleanup) el.__hhUgcCleanup(); el.__hhUgcReady = false; init(el); }
    });
    /* section removed / re-rendered: release rAF, observers and document listeners */
    document.addEventListener('shopify:section:unload', function (e) {
      var el = sectionRoot(e);
      if (el && el.__hhUgcCleanup) el.__hhUgcCleanup();
    });
  }
})();
